import {
  HomeRounded as HomeIcon,
  Groups as AffiliateIcon,
  PhoneAndroidRounded as MobileAppIcon,
  RecordVoiceOverRounded as VoiceCloningIconSidebar,
  TheatersRounded as DubbingIconMobileSidebar,
  ShoppingCartRounded as PaymentIconSidebar,
  GraphicEqRounded as TTSIconMobileSidebar,
  CodeRounded as AIVoiceAPIIcon,
  MovieFilterRounded as MovieFilterRoundedIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { SMS_IDENTITY_PROVIDER } from '@src/constants';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { VBEE_CONSOLE_URL } from '@src/configs';
import routes from '@src/constants/route';
import useFeatureFlags from '@src/hooks/useFeatureFlags';

import { useCheckDubbingRoute } from './useCheckDubbingRoute';

const useSidebarRoutes = () => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const { identityProvider } = useSelector((state) => state.auth.user);
  const isSMSUser = Object.values(SMS_IDENTITY_PROVIDER).includes(
    identityProvider,
  );

  const useRenameMergeProduct = getFeatureValue(
    FEATURE_KEYS.RENAME_MERGE_PRODUCT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const useVoiceCloningHomePage = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING_HOMEPAGE,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );

  const projectEnabled = getFeatureValue(FEATURE_KEYS.PROJECT, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const isUserInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const useIntroduceMobileAppV2 = getFeatureValue(
    FEATURE_KEYS.INTRODUCE_NEW_MOBILE_APP,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const getStudioProductName = () => {
    if (isSMSUser) return 'aivoiceStudio';
    return useRenameMergeProduct ? 'newStudioName' : 'STUDIO';
  };

  const getDubbingProductName = () => {
    if (!isSMSUser && useRenameMergeProduct) return 'newDubbingName';
    return 'DUBBING';
  };

  const sidebarRoutes = [
    {
      name: 'home',
      path: routes.HOME,
      isMobileSidebar: false,
      isDesktopSidebar: true,
      labelSidebar: 'home',
      iconSidebar: HomeIcon,
    },
    {
      name: 'project',
      path: routes.PROJECT,
      isDesktopSidebar: projectEnabled,
      labelSidebar: 'project',
      iconSidebar: MovieFilterRoundedIcon,
      beta: true,
    },
    {
      name: 'convertText',
      path: routes.TTS,
      isMobileSidebar: true,
      isDesktopSidebar: false,
      isCreateProject: true,
      labelSidebar: getStudioProductName(),
      iconSidebar: TTSIconMobileSidebar,
    },
    {
      name: 'myVoice',
      path: routes.VOICE_CLONING_VOICES,
      isMobileSidebar:
        !isSMSUser && (useVoiceCloningHomePage || isUserInFeatureVoiceCloning),
      isDesktopSidebar:
        !isSMSUser && (useVoiceCloningHomePage || isUserInFeatureVoiceCloning),
      labelSidebar: useVoiceCloningHomePage ? 'voiceCloningLabel' : 'myVoice',
      iconSidebar: VoiceCloningIconSidebar,
      beta: true,
    },
    {
      name: 'dubbing',
      path: routes.DUBBING,
      isMobileSidebar: true,
      isDesktopSidebar: false,
      isCreateProject: true,
      labelSidebar: getDubbingProductName(),
      iconSidebar: DubbingIconMobileSidebar,
      featureKey: FEATURE_KEYS.DUBBING,
    },
    {
      name: 'voiceCloning',
      path: `${routes.VOICE_CLONING_VOICES}?show-qrcode=true`,
      isMobileSidebar: false,
      isDesktopSidebar: false,
      isCreateProject:
        !isSMSUser && isUserInFeatureVoiceCloning && !useVoiceCloningHomePage,
      labelSidebar: 'voiceCloningLabel',
      iconSidebar: VoiceCloningIconSidebar,
      beta: true,
    },
    {
      name: 'api',
      path: VBEE_CONSOLE_URL,
      externalUrl: true,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'API',
      iconSidebar: AIVoiceAPIIcon,
    },
    {
      name: 'buyPackage',
      path: isDubbingRoute ? routes.PAYMENT_DUBBING : routes.PAYMENT,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'buyPackage',
      iconSidebar: PaymentIconSidebar,
      dataId: 'open-view-plan',
    },
    {
      name: 'affiliate',
      path: routes.AFFILIATE,
      subPaths: [routes.AFFILIATE_DETAIL],
      isMobileSidebar: false,
      isDesktopSidebar: !isSMSUser ?? true,
      labelSidebar: 'affiliate',
      iconSidebar: AffiliateIcon,
    },
    {
      name: 'mobileApp',
      path: routes.MOBILE_APP,
      isMobileSidebar: useIntroduceMobileAppV2,
      isDesktopSidebar: true,
      labelSidebar: 'mobileApp',
      iconSidebar: MobileAppIcon,
    },
  ];

  const mobileSidebarRoutes = sidebarRoutes.filter(
    (route) => route.isMobileSidebar,
  );
  const desktopSidebarRoutes = sidebarRoutes.filter(
    (route) => route.isDesktopSidebar,
  );
  const newProjectRoutes = sidebarRoutes.filter(
    (route) => route?.isCreateProject,
  );

  return { mobileSidebarRoutes, desktopSidebarRoutes, newProjectRoutes };
};

export default useSidebarRoutes;
